import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  conflictRowStyle,
  formatDateOnly,
  formatNumber,
  toNumberOrNull,
} from "./seleccionBobinasUtils";

/**
 * One stock-candidate row for a SalesLine in the "Seleccion de bobinas" grid.
 * Renders the Completa/Usa resto/Deja resto checkboxes (mutually exclusive,
 * enforced by the backend) and, when the candidate is selected, up to 3
 * delegacion (branch) split inputs.
 *
 * Note on fields not available from the backend DTO
 * (CmtSeleccionBobinasCandidate): the original functional spec mentioned
 * "confirmado" and a raw "coste" (unit cost) column; the real backend does
 * not expose either (only the already-computed margenPercent/mermaPercent),
 * so those two columns are intentionally omitted here.
 */
export default function CandidateRow({
  candidate,
  itemAttributes,
  existingSelection,
  colorsDisabled,
  canEdit,
  compact,
  onToggleFlag,
  onDelegacionSave,
  busy,
}) {
  const { t } = useTranslation();
  const selCompleta = Boolean(existingSelection?.selcompleta);
  const selResto = Boolean(existingSelection?.selresto);
  const selParcial = Boolean(existingSelection?.selparcial);
  const isSelected = selCompleta || selResto || selParcial;

  const [delegaciones, setDelegaciones] = useState(["", "", ""]);
  const [percentages, setPercentages] = useState(["", "", ""]);

  useEffect(() => {
    setDelegaciones([
      existingSelection?.delegacion1 || "",
      existingSelection?.delegacion2 || "",
      existingSelection?.delegacion3 || "",
    ]);
    setPercentages([
      existingSelection?.pctdelegacion1 ?? "",
      existingSelection?.pctdelegacion2 ?? "",
      existingSelection?.pctdelegacion3 ?? "",
    ]);
  }, [existingSelection?.recId, existingSelection?.delegacion1, existingSelection?.delegacion2, existingSelection?.delegacion3,
    existingSelection?.pctdelegacion1, existingSelection?.pctdelegacion2, existingSelection?.pctdelegacion3]);

  const rowStyle = conflictRowStyle(candidate.conflict, colorsDisabled);

  const tipoLabel = candidate.tipo === "PENDIENTE_RECIBIR"
    ? t("seleccionBobinas.general.tipoPendienteRecibir")
    : t("seleccionBobinas.general.tipoDisponibleFisico");

  const batchOrTrans = candidate.inventBatchId || candidate.inventTransId || "";
  const expectedOrAntique = candidate.expectedDate
    ? { label: t("seleccionBobinas.general.expectedDate"), value: formatDateOnly(candidate.expectedDate) }
    : { label: t("seleccionBobinas.general.antiqueDate"), value: formatDateOnly(candidate.antiqueDate) };

  const handleCheckbox = (flag) => (event) => {
    const checked = event.target.checked;
    onToggleFlag(candidate, flag, checked);
  };

  const saveDelegacion = () => {
    const cleanedDelegaciones = delegaciones.map((d) => String(d || "").trim()).filter(Boolean);
    if (cleanedDelegaciones.length === 0) return;
    const cleanedPercentages = percentages
      .slice(0, cleanedDelegaciones.length)
      .map((p) => toNumberOrNull(p))
      .filter((p) => p !== null);
    onDelegacionSave(existingSelection, {
      delegaciones: cleanedDelegaciones,
      percentages: cleanedPercentages.length === cleanedDelegaciones.length ? cleanedPercentages : undefined,
    });
  };

  const kgFor = (index) => {
    const pct = toNumberOrNull(percentages[index]);
    if (pct === null) return "";
    const base = toNumberOrNull(candidate.qtyAvailable) || 0;
    return formatNumber((base * pct) / 100, 2);
  };

  if (compact) {
    return (
      <tr style={rowStyle} title={candidate.conflict}>
        <td>{tipoLabel}</td>
        <td>{candidate.itemId}{itemAttributes?.itemname ? ` - ${itemAttributes.itemname}` : ""}</td>
        <td>{formatNumber(candidate.qtyAvailable)}</td>
        <td>{batchOrTrans}</td>
        <td>{isSelected ? (selCompleta ? t("seleccionBobinas.general.selCompleta") : selResto ? t("seleccionBobinas.general.selResto") : t("seleccionBobinas.general.selParcial")) : "-"}</td>
        <td>{[existingSelection?.delegacion1, existingSelection?.delegacion2, existingSelection?.delegacion3].filter(Boolean).join(", ")}</td>
      </tr>
    );
  }

  return (
    <tr style={rowStyle} title={candidate.conflict}>
      <td>{tipoLabel}</td>
      <td>{candidate.itemId}</td>
      <td>{itemAttributes?.itemname || ""}</td>
      <td>{itemAttributes?.gramaje || ""}</td>
      <td>{itemAttributes?.width ?? ""}</td>
      <td>{itemAttributes?.depth ?? ""}</td>
      <td>{formatNumber(candidate.qtyAvailable)}</td>
      <td title={expectedOrAntique.label}>{expectedOrAntique.value}</td>
      <td>{batchOrTrans}</td>
      <td>{candidate.inventLocationId || candidate.wmsLocationId || ""}</td>
      <td>{candidate.margenPercent !== null && candidate.margenPercent !== undefined ? `${formatNumber(candidate.margenPercent)}%` : ""}</td>
      <td>{candidate.mermaPercent !== null && candidate.mermaPercent !== undefined ? `${formatNumber(candidate.mermaPercent)}%` : ""}</td>
      <td>{candidate.costoTransporte !== null && candidate.costoTransporte !== undefined ? formatNumber(candidate.costoTransporte) : "-"}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        <label style={{ marginRight: 6 }}>
          <input type="checkbox" disabled={!canEdit || busy} checked={selCompleta}
            onChange={handleCheckbox("selcompleta")} /> {t("seleccionBobinas.general.selCompleta")}
        </label>
        <label style={{ marginRight: 6 }}>
          <input type="checkbox" disabled={!canEdit || busy} checked={selResto}
            onChange={handleCheckbox("selresto")} /> {t("seleccionBobinas.general.selResto")}
        </label>
        <label>
          <input type="checkbox" disabled={!canEdit || busy} checked={selParcial}
            onChange={handleCheckbox("selparcial")} /> {t("seleccionBobinas.general.selParcial")}
        </label>
      </td>
      <td>
        {isSelected && existingSelection?.recId ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[0, 1, 2].map((index) => (
              <div key={index} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input
                  type="text"
                  className="form-control"
                  style={{ width: 70 }}
                  placeholder={t("seleccionBobinas.general.delegacionCode")}
                  value={delegaciones[index]}
                  disabled={!canEdit}
                  onChange={(e) => setDelegaciones((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))}
                  onBlur={saveDelegacion}
                />
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 60 }}
                  placeholder="%"
                  value={percentages[index]}
                  disabled={!canEdit}
                  onChange={(e) => setPercentages((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))}
                  onBlur={saveDelegacion}
                />
                <span style={{ fontSize: 11, color: "#6b7280", minWidth: 60 }}>{kgFor(index)} kg</span>
              </div>
            ))}
          </div>
        ) : (
          <span style={{ color: "#9ca3af", fontSize: 12 }}>{t("seleccionBobinas.general.delegacionRequiresSelection")}</span>
        )}
      </td>
    </tr>
  );
}
